/**
 * Downscale a picked image in the browser before upload.
 *
 * Head shots get shown at a few hundred pixels at most, so shipping a 5MB
 * phone photo to the server wastes bandwidth on a venue's wifi and bloats the
 * database (which is also the backup). Resizing here keeps uploads ~50-150KB.
 */
export async function downscaleToDataUrl(
  file: File, maxEdge = 512, quality = 0.85,
): Promise<string> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    throw new Error('Please choose a JPEG, PNG or WebP image.');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('That image is over 5MB. Please choose a smaller one.');
  }

  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process that image.');
    ctx.drawImage(bitmap, 0, 0, w, h);

    // PNG keeps transparency; everything else is smaller as JPEG.
    const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    return canvas.toDataURL(mime, quality);
  } finally {
    bitmap.close();
  }
}
